import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import type {
    FlowPreview,
    PreviewMember,
    PreviewOutcome,
    PreviewStep
} from 'crqlar-react-common/dist/models/automation/flowPreview.model';
import {isTriggerNode} from '../config/nodeCategories';

// Pure preview model (no React, no i18n): each card's number and who did what at a step.

export type PreviewCount =
    /** `value` people (messages, on a send step) go on from this step. `failed`: some paths break here. */
    | {kind: 'count'; value: number; failed: boolean}
    /** Every path that reached the step breaks on it ("No survey is selected"). */
    | {kind: 'failed'}
    | {kind: 'notReached'}
    /** Decided when the flow runs (an AI decision): the preview stops here. */
    | {kind: 'notPreviewed'};

// The API previews a team email like a guest email: SENT to the member's address, or NOT_SENT with a reason.
const SEND_TYPES: NodeType[] = [NodeType.SEND_EMAIL, NodeType.SEND_TEAM_EMAIL, NodeType.SEND_PUSH];

/**
 * Any flow with a trigger can be previewed. "Now" is the API's call: an event-started flow starts nothing
 * now, so its trigger reads 0 and the steps below stay empty.
 */
export const canSimulate = (nodes: {type: NodeType}[]): boolean => nodes.some(node => isTriggerNode(node.type));

/** Stop and Wait for everyone pass nobody on, so they show no number. */
const UNCOUNTED_TYPES: NodeType[] = [NodeType.FINISH, NodeType.AFTER_EVERYONE];

export const isSendStep = (type: NodeType): boolean => SEND_TYPES.includes(type);

/**
 * Always "how many go on from this step" (messages on a send step, everyone an If sorts).
 * Null when the card shows no number: schedule and manual triggers, Stop, Wait for everyone.
 */
export const previewCount = (nodeId: string, type: NodeType, preview: FlowPreview): PreviewCount | null => {
    if (preview.unsupported) return null;

    const fanOut = preview.fanOuts.find(entry => entry.nodeId === nodeId);
    const node = preview.nodes.find(entry => entry.nodeId === nodeId);
    const failed = (node?.outcomes.FAILED ?? 0) > 0;
    if (fanOut) return {kind: 'count', value: fanOut.total ?? fanOut.evaluated, failed};

    if (isTriggerNode(type) || UNCOUNTED_TYPES.includes(type)) return null;
    if (!node) return {kind: 'notReached'};

    if (failed && node.continued === 0 && !node.recipients) return {kind: 'failed'};
    if ((node.outcomes.NOT_PREVIEWED ?? 0) > 0 && node.continued === 0) return {kind: 'notPreviewed'};

    if (isSendStep(type)) return {kind: 'count', value: node.recipients ?? 0, failed};
    if (type === NodeType.IF_CONDITION) return {kind: 'count', value: node.reached, failed};
    return {kind: 'count', value: node.continued, failed};
};

export interface PreviewEntry {
    /** Absent for a step the run takes once. */
    member?: PreviewMember;
    step: PreviewStep;
}

/** Every path's step at [nodeId]: the members' and, for a step that runs once, the run's own. */
export const entriesAt = (preview: FlowPreview, nodeId: string): PreviewEntry[] => [
    ...preview.once.filter(step => step.nodeId === nodeId).map(step => ({step})),
    ...preview.members.flatMap(member => member.steps
        .filter(step => step.nodeId === nodeId)
        .map(step => ({member, step}))),
];

export const withOutcome = (entries: PreviewEntry[], ...outcomes: PreviewOutcome[]): PreviewEntry[] =>
    entries.filter(entry => outcomes.includes(entry.step.outcome));

/** The trigger's own entry when its runs are its events: the reminders due, or none for an event trigger. */
export const eventFanOut = (preview: FlowPreview) =>
    preview.fanOuts.find(entry => entry.nodeId === preview.triggerNodeId);

/** Whether the card's people dialog would list anybody: a fan-out's members, else every path that reached it (failures too). */
export const hasPeopleBehind = (preview: FlowPreview, nodeId: string): boolean =>
    preview.fanOuts.some(entry => entry.nodeId === nodeId)
        ? preview.members.some(member => member.fanOutNodeId === nodeId)
        : entriesAt(preview, nodeId).length > 0;

export const memberName = (member?: PreviewMember): string => member?.name?.trim() || member?.email || '';
